import React from 'react';
import { ArrowRight } from 'lucide-react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import Breadcrumbs from '@/components/Breadcrumbs';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { EnquireButton } from '@/components/CTAButtons';
import { IMAGES } from '@/data/siteData';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    name: '1 BHK',
    slug: '/1-bhk',
    carpet: '406 – 453 sq.ft. carpet',
    text: 'Efficient single-bedroom layouts in two size options, each with a balcony. Detailed unit plans with dimensions are shared on request with the cost sheet.',
  },
  {
    name: '2 BHK',
    slug: '/2-bhk',
    carpet: '637 sq.ft. carpet',
    text: 'Family-oriented two-bedroom layouts with a balcony, planned for cross-ventilation and practical storage. Request the detailed floor plan for the exact wing and stack.',
  },
  {
    name: '2.5 BHK',
    slug: '/2-5-bhk',
    carpet: '811 sq.ft. carpet',
    text: 'The largest layout, with an additional flexible room and a balcony. Unit plans showing the flexible space configuration are available on request.',
  },
];

const FloorPlans = () => (
  <>
    <Seo
      title="Provident Palm Vista Floor Plan | 1, 2 & 2.5 BHK"
      description="Provident Palm Vista floor plans — 1 BHK (406–453 sq.ft.), 2 BHK (637 sq.ft.) and 2.5 BHK (811 sq.ft.) layouts with balconies, plus the master plan of the 16-acre development."
      path="/provident-palm-vista-floor-plan"
      jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Floor Plans', path: '/provident-palm-vista-floor-plan' }])]}
    />
    <section className="pt-28 md:pt-36 pb-16 bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Floor Plans' }]} />
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">Layouts & Master Plan</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium text-palm mt-3 leading-[1.08] tracking-tight">Provident Palm Vista Floor Plan</h1>
          <p className="text-[15px] text-ink/75 mt-5 max-w-3xl leading-relaxed">
            Provident Palm Vista offers three configurations — 1, 2 and 2.5 BHK — all with balconies. Below is the master plan of the 16-acre development and a summary of each layout. Detailed unit floor plans with dimensions are shared by our team along with the latest cost sheet.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <h2 className="font-display text-2xl sm:text-3xl font-medium text-palm mb-5">Master Plan</h2>
          <div className="bg-white border border-line rounded-3xl p-4 sm:p-6 shadow-sm">
            <img src={IMAGES.masterPlan} alt="Provident Palm Vista master plan — residential wings A1–D4, clubhouse and amenity spine along Kalyan-Shilphata Road" className="w-full rounded-2xl" loading="lazy" width={664} height={849} />
            <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
              The master plan organises the residential wings around a central clubhouse and amenity spine, with homes facing city views towards Kalyan–Shilphata Road and hill views on the opposite side.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div className="h-full bg-white border border-line rounded-3xl p-7 hover:shadow-xl hover:border-gold/40 transition-all">
                <h2 className="font-display text-3xl font-medium text-palm">{p.name} <span className="text-base text-muted-foreground font-sans font-normal">floor plan</span></h2>
                <p className="text-xs font-semibold text-gold mt-1">{p.carpet}</p>
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{p.text}</p>
                <div className="flex flex-wrap gap-2 mt-6">
                  <EnquireButton label="Get Floor Plan" testid={`floorplan-${p.name.replace(/\s|\.|-/g, '').toLowerCase()}-btn`} className="!px-4 !py-2.5 !text-xs" />
                  <Link to={p.slug} className="inline-flex items-center gap-1.5 rounded-full border border-palm/25 text-palm px-4 py-2.5 text-xs font-semibold hover:border-gold hover:text-gold transition-all">
                    {p.name} details <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 bg-paper border border-line rounded-2xl p-6">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Note: Floor plans, dimensions and carpet areas are indicative and subject to applicable approvals and the final agreement. Contact us for the latest official floor plan of a specific unit, wing and floor.
          </p>
        </Reveal>
      </div>
    </section>
    <CTAPanel title="Get Detailed Floor Plans on WhatsApp" />
  </>
);

export default FloorPlans;
